import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import {
  OAuthProvider,
  getRedirectResult,
  signInWithRedirect,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../firebase';

const PNCC_MICROSOFT_TENANT_ID = import.meta.env.VITE_PNCC_MICROSOFT_TENANT_ID;

const provider = new OAuthProvider('microsoft.com');
provider.setCustomParameters({ tenant: PNCC_MICROSOFT_TENANT_ID });

const usePopupSignIn =
  import.meta.env.DEV ||
  (typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'));

interface AuthContextType {
  user: User | null;
  permissions: string[];
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

function permissionsFromClaims(claims: Record<string, unknown>): string[] {
  const raw = claims.permissions;
  if (!Array.isArray(raw)) return [];
  return raw.filter((value): value is string => typeof value === 'string');
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const redirectResult = usePopupSignIn
      ? Promise.resolve(null)
      : getRedirectResult(auth).catch((err) => {
          console.error('Redirect sign-in failed:', err);
        });

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      await redirectResult;
      if (u) {
        const tokenResult = await u.getIdTokenResult();
        setPermissions(permissionsFromClaims(tokenResult.claims));
      } else {
        setPermissions([]);
      }
      setUser(u);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signIn = async () => {
    if (usePopupSignIn) {
      await signInWithPopup(auth, provider);
    } else {
      await signInWithRedirect(auth, provider);
    }
  };

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const getIdToken = async () => {
    if (!user) return null;
    return user.getIdToken();
  };

  return (
    <AuthContext.Provider value={{ user, permissions, loading, signIn, signOut, getIdToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
